/* ==========================================================================
   fk-video.js — the shoppable clips under Add to cart on the product page.

   Two jobs.

   1. THE THUMBNAIL LOOPS. Liquid renders each thumbnail as a muted <video>
      with NO src — the URL rides on data-src. Nothing downloads until the row
      is actually on screen, and then only for the thumbnails that are. A
      shopper who never scrolls past the buy button pays nothing for this
      feature. Playback also stops when the tab is hidden, when the viewer is
      open over the top, and when the row scrolls away again.

      Three conditions suppress the loops entirely, leaving the poster frame:
      prefers-reduced-motion, Save-Data, and a browser that refuses the
      autoplay (older iOS in Low Power Mode does). The poster is a still from
      the video, so the row looks intentional either way — this degrades to
      a plain image grid, not to three black rectangles.

   2. THE EXPANDED VIEWER. Choosing a clip, loading its sources on demand,
      moving between clips, and handing "Add to cart" back to the real
      product form.
   ========================================================================== */
(function () {
  'use strict';

  /* Both are read once. Save-Data does not change mid-session, and a
     reduced-motion change fires a full re-evaluation through matchMedia
     below rather than needing a re-read here. */
  var reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };

  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && c.saveData);
  }

  function init(root) {
    if (!root || root.dataset.fkClipsBound) return;
    root.dataset.fkClipsBound = 'true';

    var viewer = root.querySelector('[data-fk-clipviewer]');
    var video = root.querySelector('[data-fk-clip-video]');
    var titleEl = root.querySelector('[data-fk-clip-title]');
    var countEl = root.querySelector('[data-fk-clip-count]');
    var dataEl = root.querySelector('[data-fk-clip-data]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-fk-clip-loop]'));

    /* ----------------------------------------------------------------------
       PORTAL THE VIEWER TO <body>.

       The viewer is `position: fixed; z-index: 1100`, which only means
       "above everything" while it is a child of the root stacking context.
       It is authored INSIDE the clips root so this file can find it, and on
       Custom & Events that root sits inside `.fk-enquiry__copy`, which is
       `position: sticky` — and a sticky element CREATES A STACKING CONTEXT
       whatever its z-index. That capped the whole viewer at the sticky
       column's level, so the open modal painted UNDERNEATH the page: probing
       the panel's centre returned .fk-custom-hero__body, its top-left
       returned the H1, and the scrim returned a nav link.

       Re-parenting to <body> is the standard fix and it is safe here: every
       reference above is already held as an element, so nothing re-queries,
       and all the viewer's CSS is flat class selectors that never depend on
       an ancestor. It also immunises the other three surfaces against any
       future transform/filter/sticky wrapper, which is the same trap.

       dataEl deliberately stays put — it is read once, below, and is a
       sibling of the viewer rather than a child.
       ---------------------------------------------------------------------- */
    if (viewer && viewer.parentNode !== document.body) {
      viewer.setAttribute('data-fk-clip-portaled', '');
      viewer._fkClipsRoot = root;
      document.body.appendChild(viewer);
    }

    /* ----------------------------------------------------------------------
       1. Thumbnail loops
       ---------------------------------------------------------------------- */

    var inView = [];        /* thumbnails currently on screen */
    var suppressed = document.visibilityState === 'hidden'; /* viewer open, or tab hidden */

    function allowLoops() {
      return !reduceMotion.matches && !saveData();
    }

    /* A thumbnail is IDLE when it is showing a still it will NOT animate out
       of: motion is off at the OS or browser level, or this clip's play() was
       refused. Paused because it scrolled off screen, or because the viewer
       opened, is deliberately NOT idle — those resume on their own, and
       flashing a play button through them would be noise.

       This drives the idle-only play glyph (see snippets/fk-clip-thumb). The
       glyph used to be removed in Liquid whenever `hide_play` was set, which
       meant a shopper on reduced motion, Save-Data or Low Power Mode got a
       still picture with nothing to press and no way to reach the clip. */
    /* The glyph is VISIBLE BY DEFAULT and hidden only by proof that pixels are
       moving. That direction is deliberate.

       It used to be the other way round — `hide_play` deleted the glyph in
       Liquid — and the failure mode was silent: reduced motion, Save-Data, iOS
       Low Power Mode, a refused play(), a stalled source or an observer that
       never fired all leave a still frame on screen, and there was then nothing
       to press. An earlier attempt at this fix armed a timer inside playThumb,
       which was no better: if the observer never fires, playThumb never runs
       and the timer is never set. Anything that keys off the script SUCCEEDING
       cannot rescue the case where the script does nothing.

       So CSS shows the glyph after a short delay unless this flag says the clip
       is playing, and the flag is only ever set by the `playing` event. If this
       whole file fails to run, the glyph is simply there — which is the safe
       direction to fail in.

       Scroll-out and viewer-open pauses deliberately do NOT clear it: those
       resume on their own, and flashing a button through them is the furniture
       `hide_play` exists to avoid. Only terminal states clear it. */
    function markPlaying(v, playing) {
      var thumb = v.closest ? v.closest('.fk-clips__thumb') : null;
      if (!thumb) return;
      if (playing) thumb.setAttribute('data-fk-clip-playing', '');
      else thumb.removeAttribute('data-fk-clip-playing');
    }

    /* Attach the source the first time this thumbnail is needed and not
       again. `load()` is required — a <video> that had no src at parse time
       will not pick one up without it. */
    function attach(v) {
      if (v.dataset.fkClipReady) return;
      var src = v.getAttribute('data-src');
      if (!src) return;

      /* The 480p rung in data-src is a GUESS. Shopify puts the bitrate its
         encoder happened to choose into the filename, and that differs per
         upload — SD-480p-1.5Mbps on one clip, 1.0 or 0.9 on another — so the
         right URL cannot be derived from the 1080p one. fk-clip-video emits
         the plausible spellings cheapest-first in data-src-fallback, ending
         with the URL Liquid actually gave us, which is always real. A miss
         costs one 404 and we step down the list. */
      var listed = (v.getAttribute('data-src-fallback') || '').split(' ');
      var alts = [];
      for (var i = 0; i < listed.length; i++) {
        if (listed[i]) alts.push(listed[i]);
      }
      v.fkAlts = alts;

      /* Bound once, here, because this is the only place that runs exactly
         once per video. `playing` is the single honest signal that pixels are
         moving — it is what hides the glyph again, not play() resolving, which
         only means the request was accepted. */
      v.addEventListener('playing', function () { markPlaying(v, true); });
      v.addEventListener('error', function () {
        if (v.fkAlts && v.fkAlts.length) {
          v.src = v.fkAlts.shift();
          v.load();
          /* Only chase it if the tile is still on screen and still allowed to
             move. Otherwise leave it loaded and let syncThumbs decide later. */
          if (inView.indexOf(v) !== -1 && !suppressed && allowLoops()) {
            var p = v.play();
            if (p && p.then) p.then(null, function () {});
          }
          return;
        }
        /* Nothing left to try. The poster stays up — a still frame, not a
           black rectangle. */
        markPlaying(v, false);
      });

      v.src = src;
      v.dataset.fkClipReady = 'true';
      v.load();
    }

    function playThumb(v) {
      /* Per-tile opt-out (a collection frame with "Play automatically" off).
         Same outcome as motion being switched off globally: it never moves, so
         the glyph stays up and the tile is still openable. */
      if (v.hasAttribute('data-fk-clip-noautoplay')) { markPlaying(v, false); return; }
      if (!allowLoops()) { markPlaying(v, false); return; }
      if (suppressed) return;
      attach(v);
      var p = v.play();
      if (p && p.then) {
        p.then(null, function () {
          /* A play() that rejects because the src 404d is the fallback
             ladder's business — the error handler is already stepping down.
             Only give up once there is genuinely nothing left to try. */
          if (v.fkAlts && v.fkAlts.length) return;
          /* Refused. The poster stays up and we stop asking — retrying on
             every scroll would just burn cycles for the same answer. */
          v.dataset.fkClipRefused = 'true';
          markPlaying(v, false);
        });
      }
    }

    function syncThumbs() {
      inView.forEach(function (v) {
        if (suppressed || !allowLoops()) {
          if (typeof v.pause === 'function') v.pause();
          /* Suppressed is transient — the viewer is open on top, or the tab is
             hidden. Nobody is looking, and it resumes by itself. */
          if (!allowLoops()) markPlaying(v, false);
        } else if (v.dataset.fkClipRefused) {
          markPlaying(v, false);
        } else {
          playThumb(v);
        }
      });
    }

    if (thumbs.length) {
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            var at = inView.indexOf(v);
            if (entry.isIntersecting) {
              if (at === -1) inView.push(v);
              playThumb(v);
            } else {
              if (at !== -1) inView.splice(at, 1);
              if (typeof v.pause === 'function') v.pause();
            }
          });
        }, {
          /* Half the thumbnail visible. A clip that is one pixel into the
             viewport is not something anyone is watching. rootMargin gives
             it a moment to buffer before it is actually looked at. */
          threshold: 0.5,
          rootMargin: '100px 0px'
        });
        thumbs.forEach(function (v) { io.observe(v); });
      } else {
        /* No IntersectionObserver: attach and play everything once. This is
           the old-Safari path, and there are three short clips at most. */
        thumbs.forEach(function (v) { inView.push(v); playThumb(v); });
      }

      /* A hidden tab should not be decoding video. */
      document.addEventListener('visibilitychange', function () {
        suppressed = document.visibilityState === 'hidden' || !viewerClosed();
        syncThumbs();
      });

      /* Someone turning reduced-motion on mid-session gets it honoured. */
      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', syncThumbs);
      } else if (reduceMotion.addListener) {
        reduceMotion.addListener(syncThumbs);
      }
    }

    /* ----------------------------------------------------------------------
       2. The viewer
       ---------------------------------------------------------------------- */

    function viewerClosed() {
      return !viewer || viewer.hasAttribute('hidden');
    }

    if (!viewer || !video || !dataEl) return;

    var clips = [];
    try {
      clips = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      /* Malformed JSON means the row still works as thumbnails; only the
         viewer is lost. Failing quietly beats throwing on every PDP. */
      return;
    }
    if (!clips.length) return;

    var index = 0;
    var lastFocus = null;

    function show(i) {
      if (i < 0) i = clips.length - 1;
      if (i >= clips.length) i = 0;
      index = i;
      var clip = clips[index];

      /* An IMAGE tile. The Instagram grid mixes stills with clips, so the stage
         swaps between the two rather than the section carrying two viewers. */
      var still = viewer.querySelector('[data-fk-clip-image]');
      if (clip.type === 'image') {
        video.pause();
        while (video.firstChild) video.removeChild(video.firstChild);
        video.removeAttribute('src');
        video.hidden = true;
        if (still) { still.src = clip.image || ''; still.alt = clip.caption || ''; still.hidden = false; }
        if (titleEl) titleEl.textContent = clip.caption || '';
        if (countEl) countEl.textContent = 'Post ' + (index + 1) + ' of ' + clips.length;
        paint(clip.product);
        permalink(clip.permalink);
        return;
      }
      if (still) { still.hidden = true; still.removeAttribute('src'); }
      video.hidden = false;

      video.pause();
      while (video.firstChild) video.removeChild(video.firstChild);
      video.removeAttribute('src');

      if (clip.poster) video.setAttribute('poster', clip.poster);
      (clip.sources || []).forEach(function (s) {
        if (!s || !s.url) return;
        var el = document.createElement('source');
        el.src = s.url;
        if (s.mime) el.type = s.mime;
        video.appendChild(el);
      });

      video.load();
      var played = video.play();
      if (played && played.catch) played.catch(function () { /* autoplay may be refused; controls are there */ });

      if (titleEl) titleEl.textContent = clip.caption || '';
      if (countEl) countEl.textContent = 'Clip ' + (index + 1) + ' of ' + clips.length;

      paint(clip.product);
      permalink(clip.permalink);
    }

    /* The link to the real Instagram post, when the tile carries one. */
    function permalink(url) {
      var a = viewer.querySelector('[data-fk-clip-permalink]');
      if (!a) return;
      if (url) { a.href = url; a.hidden = false; }
      else { a.removeAttribute('href'); a.hidden = true; }
    }

    /* Every clip carries its own product, so the buy panel is rebuilt on each
       show rather than rendered once. A Customizer clip can be attached to any
       product; a clip from a product's own metafield carries that product. */
    function paint(p) {
      var link = viewer.querySelector('[data-fk-clip-product]');
      var addBtn = viewer.querySelector('[data-fk-clip-add]');

      /* No product tagged on this tile — hide the whole buy panel rather than
         leave an empty card and a dead button. Grid tiles are often just a nice
         photo. */
      var has = !!(p && p.id);
      if (link) link.hidden = !has;
      if (addBtn) addBtn.hidden = !has;
      if (!has) return;
      var img = viewer.querySelector('[data-fk-clip-img]');
      var tier = viewer.querySelector('[data-fk-clip-tier]');
      var name = viewer.querySelector('[data-fk-clip-name]');
      var price = viewer.querySelector('[data-fk-clip-price]');
      var add = viewer.querySelector('[data-fk-clip-add]');

      if (link) link.href = p.url || '#';
      if (img) {
        if (p.image) { img.src = p.image; img.alt = p.imageAlt || ''; img.hidden = false; }
        else { img.removeAttribute('src'); img.hidden = true; }
      }
      /* hidden, not empty text — an empty <span> still occupies its line-height
         and left a ragged gap above the title when a product had no tier. */
      if (tier) { tier.textContent = p.tier || ''; tier.hidden = !p.tier; }
      if (name) name.textContent = p.title || '';
      if (price) price.textContent = p.price || '';

      if (add) {
        add.disabled = !p.available;
        /* The label is a section setting, so it is read off the element once and
           remembered — never hardcoded here. */
        if (!add.dataset.fkLabel) add.dataset.fkLabel = (add.textContent || '').trim() || 'Add to cart';
        add.textContent = p.available ? add.dataset.fkLabel : 'Sold out';
      }
    }

    function open(i, trigger) {
      lastFocus = trigger || document.activeElement;
      viewer.removeAttribute('hidden');
      document.documentElement.classList.add('fk-clips-open');
      suppressed = true;
      syncThumbs();
      show(i);
      /* Must be the BUTTON, not the scrim — the scrim also carries
         data-fk-clip-close, sits first in the DOM, and is a div, so focusing
         it silently does nothing and the dialog opens with focus still out
         on the page behind it. */
      var closeBtn = viewer.querySelector('button[data-fk-clip-close]');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      video.pause();
      viewer.setAttribute('hidden', '');
      document.documentElement.classList.remove('fk-clips-open');
      suppressed = document.visibilityState === 'hidden';
      syncThumbs();
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }

    function onClipClick(e) {
      var openBtn = e.target.closest('[data-fk-clip-open]');
      if (openBtn) {
        e.preventDefault();
        open(parseInt(openBtn.getAttribute('data-fk-clip-open'), 10) || 0, openBtn);
        return;
      }
      if (e.target.closest('[data-fk-clip-close]')) { close(); return; }
      if (e.target.closest('[data-fk-clip-prev]')) { show(index - 1); return; }
      if (e.target.closest('[data-fk-clip-next]')) { show(index + 1); return; }

      var add = e.target.closest('[data-fk-clip-add]');
      if (add && !add.disabled) {
        addCurrentClip(add);
      }
    }

    /* Bound to BOTH halves. The thumbnails stay in the clips root but the
       viewer is portaled to <body>, so a click inside the viewer no longer
       bubbles through the root — delegating on the root alone silently killed
       close, prev, next and add-to-cart. The two subtrees are disjoint after
       the portal, so one handler on each cannot double-fire. */
    root.addEventListener('click', onClipClick);
    if (viewer) viewer.addEventListener('click', onClipClick);

    function status(msg) {
      var s = viewer.querySelector('[data-fk-clip-status]');
      if (s) s.textContent = msg;
    }

    function addCurrentClip(btn) {
      var p = clips[index] && clips[index].product;
      if (!p) return;

      /* THE CLIP'S PRODUCT IS THIS PAGE'S PRODUCT.
         Hand off to the real form so the shopper's variant selection is
         honoured — the form knows which variant is chosen and this does not.
         fk-ui.js intercepts the submit, adds over the Cart API, re-renders the
         drawer and opens it. The viewer closes first so two overlays are never
         stacked. */
      if (p.current) {
        var form = document.querySelector('.fk-pdp__form');
        close();
        if (form) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
        return;
      }

      /* A DIFFERENT PRODUCT. There is no form for it on this page, so the
         chosen variant goes over the Cart API and the drawer is re-rendered
         through Dawn's own path — the same route fk-cart.js uses for the
         cart's add-ons, so the drawer, the totals and the gift meter cannot
         disagree afterwards. */
      if (!p.variantId) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      status('Adding ' + (p.title || 'item') + ' to your cart.');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: p.variantId, quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () {
          btn.disabled = false;
          btn.textContent = label;
          close();
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            return fetch('/cart?sections=cart-drawer,cart-icon-bubble')
              .then(function (r) { return r.json(); })
              .then(function (sections) {
                drawer.renderContents({ sections: sections, sections_html: sections });
                if (typeof drawer.open === 'function') drawer.open();
              });
          }
          window.location.href = '/cart';
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = label;
          status('That could not be added. Please try again.');
        });
    }

    document.addEventListener('keydown', function (e) {
      if (viewer.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft' && clips.length > 1) show(index - 1);
      else if (e.key === 'ArrowRight' && clips.length > 1) show(index + 1);
    });
  }

  function boot() {
    /* A theme-editor re-render replaces the section subtree, so the root that
       owned a portaled viewer can vanish while the viewer itself — now a child
       of <body> — survives. Drop those before binding, or each save leaves
       another orphan behind. */
    document.querySelectorAll('body > [data-fk-clip-portaled]').forEach(function (v) {
      if (!v._fkClipsRoot || !v._fkClipsRoot.isConnected) v.remove();
    });
    document.querySelectorAll('[data-fk-clips]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Re-bind after a section re-render in the theme editor. */
  document.addEventListener('shopify:section:load', boot);
})();
